import api from './api'

export type OTPVerifyRequest = {
  temp_token: string
  otp_code: string
  remember_device?: boolean
}

export type OTPResendRequest = {
  temp_token: string
}

export type OTPEnableRequest = {
  password: string
}

export type OTPDisableRequest = {
  password: string
}

export type OTPTokenResponse = {
  access_token: string
  refresh_token: string
  token_type: string
  expires_in: number
  trusted_device_token?: string
}

export type OTPResendResponse = {
  temp_token: string
  message: string
  expires_in: number
}

export type OTPStatusResponse = {
  enabled: boolean // OTP por email
  org_enforced: boolean
  enabled_at: string | null
  totp_enabled?: boolean // app autenticador (TOTP)
  recovery_codes_remaining?: number
}

// ===== TOTP (app autenticador) =====

export type TotpSetupResponse = {
  secret: string
  otpauth_uri: string
}

export type TotpEnableRequest = {
  secret: string
  code: string
}

export type TotpEnableResponse = {
  recovery_codes: string[]
}

export type TotpDisableRequest = {
  password: string
}

export type TrustedDevice = {
  id: string
  ip: string | null
  user_agent: string | null
  last_used_at: string | null
  expires_at: string | null
  created_at: string | null
  current?: boolean
}

export type OrgOtpPolicy = {
  force_otp: boolean
}

export type StepUpChallenge = {
  method: 'totp' | 'email'
  temp_token: string
  email_fallback: boolean
  expires_in: number
  message: string
}

// Token de dispositivo confiável guardado no navegador (localStorage)
export const TRUSTED_DEVICE_STORAGE_KEY = 'nebula_trusted_device'

export const getTrustedDeviceToken = (): string | null => {
  try {
    return localStorage.getItem(TRUSTED_DEVICE_STORAGE_KEY)
  } catch {
    return null
  }
}

export const setTrustedDeviceToken = (token: string | null): void => {
  try {
    if (token) localStorage.setItem(TRUSTED_DEVICE_STORAGE_KEY, token)
    else localStorage.removeItem(TRUSTED_DEVICE_STORAGE_KEY)
  } catch {
    // ignore
  }
}

export const otpApi = {
  verify: async (data: OTPVerifyRequest): Promise<OTPTokenResponse> => {
    const response = await api.post('/api/v1/auth/otp/verify', data)
    return response.data
  },

  resend: async (data: OTPResendRequest): Promise<OTPResendResponse> => {
    const response = await api.post('/api/v1/auth/otp/resend', data)
    return response.data
  },

  enable: async (data: OTPEnableRequest): Promise<{ message: string }> => {
    const response = await api.post('/api/v1/auth/otp/enable', data)
    return response.data
  },

  disable: async (data: OTPDisableRequest): Promise<{ message: string }> => {
    const response = await api.post('/api/v1/auth/otp/disable', data)
    return response.data
  },

  getStatus: async (): Promise<OTPStatusResponse> => {
    const response = await api.get('/api/v1/auth/otp/status')
    return response.data
  },

  // ===== TOTP (app autenticador) =====
  totpSetup: async (): Promise<TotpSetupResponse> => {
    const response = await api.post('/api/v1/auth/otp/totp/setup')
    return response.data
  },

  totpEnable: async (data: TotpEnableRequest): Promise<TotpEnableResponse> => {
    const response = await api.post('/api/v1/auth/otp/totp/enable', data)
    return response.data
  },

  totpDisable: async (data: TotpDisableRequest): Promise<{ message: string }> => {
    const response = await api.post('/api/v1/auth/otp/totp/disable', data)
    return response.data
  },

  // ===== Trusted devices =====
  listTrustedDevices: async (): Promise<TrustedDevice[]> => {
    const token = getTrustedDeviceToken()
    const response = await api.get('/api/v1/auth/mfa/trusted-devices', {
      headers: token ? { 'X-Trusted-Device-Token': token } : undefined,
    })
    return response.data
  },

  revokeTrustedDevice: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete(`/api/v1/auth/mfa/trusted-devices/${id}`)
    return response.data
  },

  // ===== Step-up: desafio de OTP para confirmar ação sensível =====
  stepUpChallenge: async (): Promise<StepUpChallenge> => {
    const response = await api.post('/api/v1/auth/step-up/challenge')
    return response.data
  },

  // ===== Política de 2FA da organização (force_otp) =====
  getOrgPolicy: async (): Promise<OrgOtpPolicy> => {
    const response = await api.get('/api/v1/auth/otp/org-policy')
    return response.data
  },

  // `organizationId` aplica a política a OUTRA org (só superadmin — aba Segurança do ADM,
  // #1122); sem ele, a org ativa. O backend faz o merge por chave em `settings`.
  setOrgPolicy: async (force_otp: boolean, organizationId?: string): Promise<OrgOtpPolicy> => {
    const response = await api.patch(
      '/api/v1/auth/otp/org-policy',
      { force_otp },
      organizationId ? { params: { organization_id: organizationId } } : undefined,
    )
    return response.data
  },
}
